import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  ShoppingCart, 
  Factory, 
  Users, 
  Truck, 
  ClipboardList, 
  Package, 
  UserCog, 
  Briefcase, 
  DollarSign, 
  Bell, 
  UserPlus, 
  LogOut,
  Building2,
  Settings,
  Lock,
  ClipboardCheck,
  BarChart3,
  Car,
  FolderOpen
} from 'lucide-react';

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { id: 'orcamento', label: 'Orçamento', path: '/orcamento', icon: FileText },
  { id: 'vendas', label: 'Vendas', path: '/vendas', icon: ShoppingCart },
  { id: 'ordem-compra', label: 'Gastos por Evento', path: '/ordem-compra', icon: ClipboardCheck },
  { id: 'producao', label: 'Produção', path: '/producao', icon: Factory },
  { id: 'clientes', label: 'Clientes', path: '/clientes', icon: Users },
  { id: 'fornecedores', label: 'Fornecedores', path: '/fornecedores', icon: Truck },
  { id: 'almoxarifado', label: 'Almoxarifado', path: '/almoxarifado', icon: Package },
  { id: 'itens-loja', label: 'Itens da Loja', path: '/itens-loja', icon: Building2 },
];

const secondaryItems = [
  { id: 'equipe', label: 'Equipe', path: '/equipe', icon: UserCog },
  { id: 'freelancer', label: 'Freelancer', path: '/freelancer', icon: Briefcase },
  { id: 'veiculos', label: 'Veículos', path: '/veiculos', icon: Car },
  { id: 'documentacao', label: 'Documentação', path: '/documentacao', icon: FolderOpen },
  { id: 'financeiro', label: 'Financeiro', path: '/financeiro', icon: DollarSign },
  { id: 'avisos', label: 'Avisos do Dia', path: '/avisos', icon: Bell },
];

const bottomItems = [
  { id: 'novo-usuario', label: 'Novo Usuário', path: '/novo-usuario', icon: UserPlus },
  { id: 'configuracoes', label: 'Configurações', path: '/configuracoes', icon: Settings },
  { id: 'logout', label: 'Log Out', path: '/logout', icon: LogOut },
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, logout, user } = useAuth();

  const handleItemClick = (item) => {
    if (item.id === 'logout') {
      logout();
      toast.success('Logout realizado com sucesso!');
      navigate('/login');
      return;
    }
    
    // Check permission before navigating
    if (!hasPermission(item.id) && item.id !== 'configuracoes') {
      toast.error('Você não tem permissão para acessar esta área', {
        description: 'Entre em contato com o administrador.'
      });
      return;
    }
    
    if (item.path) {
      navigate(item.path);
    }
  };

  const isActive = (path) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const renderMenuItem = (item, showLock = true) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    const hasAccess = hasPermission(item.id) || item.id === 'logout' || item.id === 'configuracoes';
    
    return (
      <div
        key={item.id}
        onClick={() => handleItemClick(item)}
        className={`relative flex items-center gap-2.5 px-3 py-1.5 text-[13px] font-medium transition-colors duration-200 cursor-pointer hover:text-primary ${
          active ? 'text-primary bg-primary/10' : 'text-foreground/80'
        } ${!hasAccess ? 'opacity-40' : ''}`}
      >
        {/* Pink highlight bar for active item */}
        {active && (
          <div className="absolute left-0 top-1 bottom-1 w-1 bg-primary rounded-r-full" />
        )}
        <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${active ? 'text-primary' : ''}`} />
        <span className="flex-1">{item.label}</span>
        {!hasAccess && showLock && (
          <Lock className="h-3 w-3 text-muted-foreground" />
        )}
      </div>
    );
  };

  // Filter menu items based on permissions
  const visibleMenuItems = menuItems;
  const visibleSecondaryItems = secondaryItems;
  const visibleBottomItems = bottomItems;

  return (
    <aside className="fixed left-4 top-4 bottom-4 w-64 bg-card/70 backdrop-blur-sm border-0 rounded-2xl flex flex-col z-50 shadow-xl shadow-black/20">
      {/* Logo */}
      <div className="p-4 pb-2 flex justify-center">
        <img 
          src="https://customer-assets.emergentagent.com/job_dark-dashboard-2/artifacts/e0nzeqpj_logo%20yoshida%20%281%29.png" 
          alt="Yoshida Eventos e Cenografia" 
          className="h-[68px] w-auto object-contain"
        />
      </div>

      <Separator className="my-1 mx-auto w-[70%] bg-border/50" />

      {/* Main Menu */}
      <nav className="flex-1 flex flex-col py-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          {visibleMenuItems.map(item => renderMenuItem(item))}
          
          <Separator className="my-1.5 mx-auto w-[70%] bg-border/50" />
          
          {visibleSecondaryItems.map(item => renderMenuItem(item))}
        </div>
        
        {/* Bottom Items */}
        <div className="mt-auto flex-shrink-0">
          <Separator className="my-1.5 mx-auto w-[70%] bg-border/50" />
          {visibleBottomItems.map(item => renderMenuItem(item, false))}
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;
